import { ToggleTextButton } from "hec/components/cta";

import { isEditing } from "@builder.io/sdk-react";

import useLearningCenter from "~/hooks/use-learning-center";

import { HERO_ROLES, buttonFunctions } from "./cta.helper";
import { computeHeroPrimary, getHeroOverrides } from "./utils";

interface HeroToggleTextButtonProps {
  condition: string;
  conditionMetText: string;
  conditionNotMetText: string;
  stateToShowInEditor: string;
  isLearningCenterHero?: string;
  scrollTargetId?: string;
  clickFunction?: string;
  isRevShare?: boolean;
  attributes?: {
    className?: string;
    [key: string]: any;
  };
  "data-testid"?: string;
  heroRole?: "take_quiz" | "continue";
}

const HeroToggleTextButton = ({
  condition,
  conditionMetText,
  conditionNotMetText,
  stateToShowInEditor,
  clickFunction = buttonFunctions.linkToPage,
  attributes,
  heroRole,
  isRevShare,
  "data-testid": dataTestId,
  scrollTargetId,
}: HeroToggleTextButtonProps) => {
  const { className: attrClassName, ...restAttributes } = attributes || {};
  const {
    learningCenterData,
    isLoading: lcLoading,
    error: lcError,
  } = useLearningCenter();
  const isEdit = isEditing();
  if (lcLoading) return null;
  if (lcError)
    console.warn(
      "Learning Center data error; falling back to defaults",
      lcError,
    );

  // Primary/secondary styling
  const heroPrimary = computeHeroPrimary(learningCenterData?.goals);
  const thisRole =
    heroRole === HERO_ROLES.CONTINUE
      ? HERO_ROLES.CONTINUE
      : HERO_ROLES.TAKE_QUIZ;
  const computedVariant = thisRole === heroPrimary ? "primary" : "secondary";
  // URL overrides
  const overrides = getHeroOverrides(
    thisRole,
    learningCenterData?.activeLessonUrl,
    learningCenterData?.activeGoal,
    scrollTargetId,
  );

  const finalRouteType = overrides?.routeType ?? null;
  const finalUrl = overrides?.url ?? ""; // let appendSearchParams build #id for element
  const clickHandler = overrides?.clickFunction ?? clickFunction;

  return (
    <ToggleTextButton
      ctaType="button"
      condition={condition}
      conditionMetText={conditionMetText}
      conditionNotMetText={conditionNotMetText}
      stateToShowInEditor={stateToShowInEditor}
      variant={computedVariant}
      clickFunction={clickHandler}
      scrollTargetId={scrollTargetId}
      routeType={finalRouteType ?? undefined}
      url={finalUrl ?? undefined}
      isEdit={isEdit}
      className={attrClassName}
      isRevShare={isRevShare}
      {...restAttributes}
      data-testid={dataTestId || "toggleTextButton-hero"}
    />
  );
};

export default HeroToggleTextButton;
